import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

import type { AreaMetrics, ImpactResult } from '../../data/impactTypes';
import type { GeoLevel } from '../../types';

import { MetricsList } from './MetricsList';
import { overviewPanelStyles as styles } from './OverviewPanel.styles';
import { SelectedAreasList } from './SelectedAreasList';

/**
 * A selected area's card data. `metrics` is `null` until the live CDN
 * dataset or the `/impact/result` response actually covers this FIPS — the
 * card shows an honest "Loading details…" state rather than a placeholder
 * number in that window.
 */
export interface AreaCard {
  fips: string;
  name: string;
  level: GeoLevel;
  metrics: AreaMetrics | null;
}

interface OverviewPanelProps {
  /** `null` while loading (first paint) or after a failed /impact/result call. */
  result: ImpactResult | null;
  resultLoading: boolean;
  resultError: string | null;
  onRetry: () => void;
  areaCards: AreaCard[];
  maxAreas: number;
  /** Remove by the area's FIPS. */
  onRemoveArea: (fips: string) => void;
  onClearAll: () => void;
}

/**
 * Desktop left sidebar — composes `MetricsList` and `SelectedAreasList`
 * (also shared by the mobile bottom sheet's tabs) under a shared header.
 */
export function OverviewPanel({
  result,
  resultLoading,
  resultError,
  onRetry,
  areaCards,
  maxAreas,
  onRemoveArea,
  onClearAll,
}: OverviewPanelProps) {
  return (
    <Box component="aside" sx={styles.root}>
      <Box sx={styles.header}>
        <Typography component="h2" sx={styles.title}>
          Overview of selected areas
        </Typography>
        <Typography sx={styles.subtitle}>
          Metric data are dynamic with selections.
        </Typography>
      </Box>

      <MetricsList
        result={result}
        resultLoading={resultLoading}
        resultError={resultError}
        onRetry={onRetry}
      />

      <Divider sx={styles.divider} />

      <SelectedAreasList
        areaCards={areaCards}
        maxAreas={maxAreas}
        onRemoveArea={onRemoveArea}
        onClearAll={onClearAll}
      />
    </Box>
  );
}
